import { useState } from 'react';
import { siteInfo } from '@/mocks/site';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9osd0aftmtnfcfh0d6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(msg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="了解可游漫画网 - 韩国漫画在线免费阅读平台。品牌介绍、口碑评价、联系方式，致力于为韩漫爱好者提供最优质的阅读体验。"
        keywords="可游漫画网,韩国漫画,关于我们,品牌介绍,联系方式,韩漫在线免费阅读"
        canonicalPath="/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 可游漫画网',
        description: '了解可游漫画网的品牌故事、核心优势和联系方式',
        url: 'https://szwlst.cn/about',
      }} />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <section className="px-4 md:px-6 bg-background-50">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-[1000px] mx-auto text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-800 mb-4">
              关于 <span className="text-primary-500">可游漫画网</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto">
              可游漫画网成立于{siteInfo.foundingYear}年，是国内领先的韩国漫画在线免费阅读平台。
              我们致力于为中文读者提供最全面、最优质的韩国漫画阅读体验，
              始终坚持提供无删减原版内容和流畅的下拉式阅读模式。
              经过多年的发展，可游漫画网已经成为数百万韩漫爱好者首选的阅读平台。
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              {[
                { icon: 'ri-stack-line', value: '12,580+', label: '漫画资源' },
                { icon: 'ri-file-copy-2-line', value: '356,000+', label: '漫画章节' },
                { icon: 'ri-user-line', value: '890万+', label: '注册用户' },
                { icon: 'ri-eye-line', value: '52万+', label: '日活跃用户' },
              ].map((stat, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-foreground-800">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1440px] mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800">我们的优势</h2>
              <p className="text-sm text-foreground-500 mt-2">用心打造最好的韩漫阅读平台</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {siteInfo.features.map((feature, i) => (
                <div key={i} className="p-5 md:p-6 rounded-xl bg-white border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-800 mb-2">{feature.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800">读者口碑</h2>
              <p className="text-sm text-foreground-500 mt-2">来自真实用户的好评反馈</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {siteInfo.testimonials.map((item, i) => (
                <div key={i} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{item.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1000px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。
                  我们的客服团队会在24小时内回复您的消息。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200">
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">客服电话</p>
                      <a href={`tel:${siteInfo.phone}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200">
                        {siteInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-4">发送消息</h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4 p-5 md:p-6 rounded-xl bg-white border border-background-200/70"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 bg-background-50 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 bg-background-50 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 bg-background-50 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言内容..."
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {/* Honeypot */}
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formState === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      消息已发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formState === 'error' && (
                    <div className="p-3 rounded-lg bg-primary-100 text-primary-800 text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError || '发送失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}